/**
 * 親パターンの項目リストをJSONへ書き出す（#3372）。
 *
 * もとは ItemListManager.tsx 内のローカル関数（buildExportItemsFrom）。
 * 子パターンのJSON入力画面でも「親パターンの設定JSON」を参照用に出すため切り出した。
 *
 * コンポーネントの状態に依存しない純粋関数で、引数の項目リストだけを見る。
 * コピーすると片方だけ直して出力が分かれるため、必ずこれを共用する。
 */

import type { ItemListType } from '../../types/common';
import { buildValidationExport } from './jsonKeyPicker';

/**
 * 項目リストを出力用のプレーンオブジェクトへ変換する。
 *
 * キーは snake_case を正とする（Firestore の保存形式・外部公開を想定。#3195）。
 * 値が設定されていないフィールドはキーごと省き、未使用の設定がJSONに現れないようにする。
 */
export const buildItemListExport = (source: ItemListType[]): Record<string, unknown>[] =>
  source
    // 値が設定された項目のみを出力（未入力の項目は除外）
    .filter(item => item.parentValue?.trim())
    .map(item => {
      const exportChildren = (item.children || [])
        .filter(child => child.childValue?.trim())
        .map(child => ({
          // #3039: 項目IDを出力する。取り込み側でIDを維持するために必須。
          // '0' は未保存の新規項目のため出力しない（取り込み時に新規として採番させる）
          ...(child.id && child.id !== '0' ? { id: child.id } : {}),
          name: child.childValue.trim(),
          type: child.type || 'string',
          // #3175: フィールドコードも引き継ぐ。含めないと複製先で項目名から再採番され、
          // コピー元のテンプレート・計算式の参照が合わなくなる
          ...(child.field_code ? { field_code: child.field_code } : {}),
          ...(child.displayWidth !== undefined ? { display_width: child.displayWidth } : {}),
          ...(child.is_active === false ? { is_active: false } : {}),
          ...(child.is_ocr_target === false ? { is_ocr_target: false } : {}),
          ...(child.extractionRule ? { extraction_rule: child.extractionRule } : {}),
          ...(child.outputFormat ? { output_format: child.outputFormat } : {}),
          ...(child.fixedValue ? { fixed_value: child.fixedValue } : {}),
          // 選択肢（#3075）。設定されているときだけ出力する
          ...(child.choices && child.choices.length > 0 ? { choices: child.choices } : {}),
          // 計算フィールド（#3145 / #3195）。計算ONのときだけ出力する
          ...(child.is_calculated ? { is_calculated: true, formula: child.formula ?? '' } : {}),
          ...(child.required ? { required: true } : {}),
          ...(child.minLength !== undefined ? { min_length: child.minLength } : {}),
          ...(child.maxLength !== undefined ? { max_length: child.maxLength } : {}),
          ...(child.minValue !== undefined ? { min_value: child.minValue } : {}),
          ...(child.maxValue !== undefined ? { max_value: child.maxValue } : {}),
          ...(child.treatZeroAsEmpty ? { treat_zero_as_empty: true } : {}),
          // 文字種・入力パターン・日付範囲・要注意選択肢（#3357 / #3372）。
          // 書き出さないと、取り込み側で読まれず設定が消える
          ...buildValidationExport(child),
        }));
      return {
        // #3039: 項目IDを出力する（上記の子項目と同じ理由）
        ...(item.id && item.id !== '0' ? { id: item.id } : {}),
        name: item.parentValue.trim(),
        type: exportChildren.length > 0 ? 'array' : item.type || 'string',
        // #3175: フィールドコードも引き継ぐ（上記の子項目と同じ理由）
        ...(item.field_code ? { field_code: item.field_code } : {}),
        ...(item.displayWidth !== undefined ? { display_width: item.displayWidth } : {}),
        ...(item.is_active === false ? { is_active: false } : {}),
        ...(item.is_ocr_target === false ? { is_ocr_target: false } : {}),
        ...(item.extractionRule ? { extraction_rule: item.extractionRule } : {}),
        ...(item.outputFormat ? { output_format: item.outputFormat } : {}),
        ...(item.fixedValue ? { fixed_value: item.fixedValue } : {}),
        // 選択肢（#3075）。設定されているときだけ出力し、未使用パターンのJSONは従来どおりにする
        ...(item.choices && item.choices.length > 0 ? { choices: item.choices } : {}),
        // 計算フィールド（#3145 / #3195）。計算ONのときだけ出力する
        ...(item.is_calculated ? { is_calculated: true, formula: item.formula ?? '' } : {}),
        ...(item.required ? { required: true } : {}),
        ...(item.minLength !== undefined ? { min_length: item.minLength } : {}),
        ...(item.maxLength !== undefined ? { max_length: item.maxLength } : {}),
        ...(item.minValue !== undefined ? { min_value: item.minValue } : {}),
        ...(item.maxValue !== undefined ? { max_value: item.maxValue } : {}),
        ...(item.treatZeroAsEmpty ? { treat_zero_as_empty: true } : {}),
        // 文字種・入力パターン・日付範囲・要注意選択肢（#3357 / #3372）。上記の子項目と同じ理由
        ...buildValidationExport(item),
        ...(exportChildren.length > 0 ? { children: exportChildren } : {}),
      };
    });
